import { useState } from "react";
import { ProviderSelector } from "@/components/music/ProviderSelector";
import { UnifiedMusicPlayer } from "@/components/music/UnifiedMusicPlayer";
import { useMusicProvider } from "@/hooks/useMusicProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Music } from "lucide-react";

const MusicProviderTest = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const {
    currentProvider,
    selectedProviderType,
    isAuthenticated,
    isInitializing,
    currentTrack,
    playerState,
    switchProvider,
    connect,
    disconnect,
    play,
    pause,
    resume,
    next,
    previous,
    seek,
    setVolume,
    searchTracks
  } = useMusicProvider();

  const handleSearch = async () => {
    if (!searchQuery.trim() || !isAuthenticated) return;
    
    setIsSearching(true);
    try {
      const results = await searchTracks(searchQuery, 10);
      setSearchResults(results);
    } catch (error) {
      console.error('Erro na busca:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePlayTrack = async (trackId: string) => {
    await play(trackId);
  };

  const handlePlayPause = async () => {
    if (playerState?.isPlaying) {
      await pause();
    } else {
      await resume();
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Music className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Player Multi-Plataforma</h1>
          </div>
          <p className="text-muted-foreground">
            Conecte-se ao Spotify, Apple Music ou Deezer
          </p>
        </div>

        {/* Provider Selector */}
        <ProviderSelector
          selectedProvider={selectedProviderType}
          isAuthenticated={isAuthenticated}
          isConnecting={isInitializing}
          onProviderChange={switchProvider}
          onConnect={connect}
          onDisconnect={disconnect}
        />

        {/* Music Player */}
        {isAuthenticated && (
          <>
            <UnifiedMusicPlayer
              currentTrack={currentTrack}
              isPlaying={playerState?.isPlaying || false}
              position={playerState?.position || 0}
              duration={playerState?.duration || 0}
              volume={playerState?.volume || 100}
              providerName={currentProvider?.name}
              onPlayPause={handlePlayPause}
              onNext={next}
              onPrevious={previous}
              onSeek={seek}
              onVolumeChange={setVolume}
            />

            {/* Search */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Buscar Músicas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Digite o nome da música ou artista..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button 
                    onClick={handleSearch}
                    disabled={isSearching || !searchQuery.trim()}
                  >
                    {isSearching ? 'Buscando...' : 'Buscar'}
                  </Button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {searchResults.length} resultados encontrados
                    </p>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {searchResults.map((track) => (
                        <div
                          key={track.id}
                          className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                        >
                          {track.album_art && (
                            <img
                              src={track.album_art}
                              alt={track.name}
                              className="w-12 h-12 rounded"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{track.name}</p>
                            <p className="text-sm text-muted-foreground truncate">
                              {track.artist}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handlePlayTrack(track.id)}
                          >
                            Tocar
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Instructions */}
        {!isAuthenticated && (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center space-y-3">
              <Music className="w-12 h-12 mx-auto text-muted-foreground" />
              <h3 className="font-semibold">Bem-vindo ao Player Multi-Plataforma</h3>
              <p className="text-sm text-muted-foreground">
                Selecione um serviço de música acima e conecte-se para começar a tocar suas músicas favoritas durante as corridas.
              </p>
              <div className="text-xs text-muted-foreground space-y-1 pt-2">
                <p>✓ Spotify: Melhor integração e mais recursos</p>
                <p>✓ Apple Music: Perfeito para usuários iOS</p>
                <p>✓ Deezer: Alternativa com plano gratuito</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MusicProviderTest;
